/* @flow */
import React from 'react';
import { inject, observer } from 'mobx-react';
import { translate } from 'react-i18next';

import type { TranslatorProps } from 'react-i18next';
import { ComponentWithInjectedProps } from 'utils/injectedProps';
import type TypeActionDrawerStore from 'stores/ActionDrawerStore';

import Form from 'forms/_.extend';
import Loading from 'components/Loading/Loading';
import ActionDrawerHeader from 'views/ActionDrawer/components/ActionDrawerHeader/ActionDrawerHeader';

import DeleteUserLocationForm from './DeleteUserLocationForm/DeleteUserLocationForm';
import DeleteUserLocationSuccess from './DeleteUserLocationSuccess/DeleteUserLocationSuccess';
import DeleteUserLocationError from './DeleteUserLocationError/DeleteUserLocationError';

import styles from '../../../ActionDrawer.module.scss';
import { getLocationValue } from '../utils/getLocationValue';

type InjectedProps = {|
  actionDrawerStore: TypeActionDrawerStore,
|} & TranslatorProps;

export type AssignOrChangeUserLocationProps = {
  user: Object,
  account: Object,
};

@translate()
@inject('actionDrawerStore')
@observer
export default class DeleteUserLocation extends ComponentWithInjectedProps<
  AssignOrChangeUserLocationProps,
  {},
  InjectedProps
> {
  createForm = () => {
    const { t, account, user, actionDrawerStore } = this.props;
    const locationValue = getLocationValue(user.location);
    const hasLocation = user.location;

    const fields = [
      {
        name: 'changeLocation',
        label: hasLocation
          ? t('general:actions.change_location_to')
          : t('general:actions.assign_user_to_location'),
        fields: [
          {
            name: 'location',
            label: t('general:location'),
            rules: 'required|string',
            value: locationValue,
            extra: {
              hierarchy: [
                {
                  name: 'company',
                  type: 'company',
                  placeholder: t('general:company'),
                  value:
                    user.location && user.location.company.id
                      ? {
                          id: user.location && user.location.company.id,
                          name: user.location && user.location.company.name,
                        }
                      : null,
                },
                {
                  name: 'country',
                  type: 'country',
                  placeholder: t('general:country'),
                  value:
                    user.location && user.location.site.country_id
                      ? {
                          id: user.location && user.location.site.country_id,
                          name: user.location && user.location.site.country_name,
                        }
                      : null,
                },
                {
                  name: 'site',
                  type: 'location',
                  placeholder: t('general:locations.labels.site'),
                  value:
                    user.location && user.location.site.id
                      ? {
                          id: user.location && user.location.site.id,
                          name: user.location && user.location.site.name,
                        }
                      : null,
                },
                {
                  name: 'building',
                  type: 'location',
                  placeholder: t('general:locations.labels.building_transport'),
                  value:
                    user.location && user.location.building.id
                      ? {
                          id: user.location && user.location.building.id,
                          name: user.location && user.location.building.name,
                        }
                      : null,
                },
                {
                  name: 'room',
                  type: 'location',
                  placeholder: t('general:locations.labels.lab_room'),
                  value:
                    user.location && user.location.room.id
                      ? {
                          id: user.location && user.location.room.id,
                          name: user.location && user.location.room.name,
                        }
                      : null,
                },
                {
                  name: 'workspace',
                  type: 'location',
                  placeholder: t('general:locations.labels.system_workspace'),
                  value:
                    user.location && user.location.workspace.id
                      ? {
                          id: user.location && user.location.workspace.id,
                          name: user.location && user.location.workspace.name,
                        }
                      : null,
                },
              ],
            },
          },
        ],
      },
    ];

    const hooks: Object = {
      onSuccess(data: Object) {
        const { location } = data.values().changeLocation;

        actionDrawerStore.removeUserLocation(account.id, user.id, location);
      },
    };

    return new Form(
      { fields },
      {
        hooks,
        name: 'DeleteUserLocation',
      }
    );
  };

  render() {
    const { t, user, account, actionDrawerStore } = this.props;
    const title = t('general:actions.remove_location');

    if (actionDrawerStore.loading) {
      return <Loading />;
    }

    if (actionDrawerStore.error) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.error}`}>
          <ActionDrawerHeader title={title} />
          <DeleteUserLocationError user={user} />
        </div>
      );
    }

    if (actionDrawerStore.success) {
      return (
        <div className={`${styles['action-drawer-form']} ${styles.success}`}>
          <ActionDrawerHeader title={title} />
          <DeleteUserLocationSuccess response={actionDrawerStore.response} />
        </div>
      );
    }

    return (
      <div className={styles['action-drawer-form']}>
        <ActionDrawerHeader title={title} />
        <DeleteUserLocationForm user={user} account={account} form={this.createForm()} />
      </div>
    );
  }
}
